import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../../lib/utils';
import { SidebarGroup } from './SidebarGroup';
import { SidebarItem } from './SidebarItem';
import type { SidebarNavItem, SidebarSlot } from './index';

type Props = {
  // 'fixed' = the persistent ≥xl rail (collapsible). 'drawer' = the <xl
  // off-canvas overlay (always expanded, dismissed via the close button).
  variant: 'fixed' | 'drawer';
  collapsed: boolean;
  onToggleCollapse: () => void;
  onClose: () => void;
  appTitle: string;
  appLogo?: ReactNode;
  appLogoCollapsed?: ReactNode;
  navItems: SidebarNavItem[];
  sidebarFooter?: SidebarSlot;
  sidebarBottomBar?: SidebarSlot;
};

export function SidebarContent({
  variant,
  collapsed,
  onToggleCollapse,
  onClose,
  appTitle,
  appLogo,
  appLogoCollapsed,
  navItems,
  sidebarFooter,
  sidebarBottomBar,
}: Props) {
  const isDrawer = variant === 'drawer';
  // The drawer is never collapsed — it always shows the full rail.
  const railCollapsed = collapsed && !isDrawer;

  const railBody = (
    <>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <ul
          className={cn('flex flex-col gap-2 py-2', railCollapsed ? 'items-center px-3' : 'px-4')}
        >
          {navItems.map((item) =>
            'children' in item ? (
              <li key={item.label} className={railCollapsed ? '' : 'w-full'}>
                <SidebarGroup
                  item={item}
                  collapsed={railCollapsed}
                  onNavigate={isDrawer ? onClose : undefined}
                />
              </li>
            ) : (
              <li key={item.path ?? item.label} className={railCollapsed ? '' : 'w-full'}>
                <SidebarItem
                  to={item.path}
                  label={item.label}
                  icon={item.icon}
                  accent={item.accent}
                  badge={item.badge}
                  disabled={item.disabled}
                  collapsed={railCollapsed}
                  end={item.end ?? item.path === '/'}
                  onNavigate={isDrawer ? onClose : undefined}
                />
              </li>
            ),
          )}
        </ul>

        {sidebarFooter && (
          <div
            className={cn(
              'mt-auto flex flex-col gap-2 py-2',
              railCollapsed ? 'items-center px-3' : 'px-4',
            )}
          >
            {sidebarFooter({ collapsed: railCollapsed, drawer: isDrawer })}
          </div>
        )}
      </div>

      {sidebarBottomBar && (
        <div
          className={cn(
            'flex h-16 shrink-0 items-center justify-center border-t border-sidebar-border p-2',
            'bg-gradient-to-t from-sidebar-card-end to-sidebar-card-start',
          )}
        >
          {sidebarBottomBar({ collapsed: railCollapsed, drawer: isDrawer })}
        </div>
      )}
    </>
  );

  // On the collapsed rail the brand mark is the expand affordance (per the DS).
  if (railCollapsed && appLogoCollapsed) {
    return (
      <>
        <div className="flex items-center justify-center p-4">
          <button
            type="button"
            data-testid="sidebar-collapse-toggle"
            aria-label="Expand sidebar"
            aria-pressed={collapsed}
            onClick={onToggleCollapse}
            className={cn(
              'group flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg',
              'transition-colors hover:bg-sidebar-neutral-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            )}
          >
            {/* Resting state is the "+" brand mark; hovering swaps it to the
             * expand-panel icon to signal the click opens the rail. */}
            <span className="flex items-center justify-center group-hover:hidden">
              {appLogoCollapsed}
            </span>
            <PanelLeftOpen className="hidden size-4 text-sidebar-foreground group-hover:block" />
          </button>
        </div>
        {railBody}
      </>
    );
  }

  return (
    <>
      <div
        className={cn(
          'flex items-center p-4',
          railCollapsed ? 'justify-center' : 'justify-between',
        )}
      >
        {!railCollapsed &&
          (appLogo ?? <span className="text-base font-semibold tracking-tight">{appTitle}</span>)}
        {isDrawer ? (
          <button
            type="button"
            data-testid="sidebar-drawer-close"
            aria-label="Close navigation"
            onClick={onClose}
            className={cn(
              'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg',
              'text-sidebar-foreground transition-colors hover:bg-sidebar-neutral-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            )}
          >
            <X className="h-5 w-5" />
          </button>
        ) : (
          <button
            type="button"
            data-testid="sidebar-collapse-toggle"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            onClick={onToggleCollapse}
            className={cn(
              'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg',
              'text-sidebar-foreground transition-colors hover:bg-sidebar-neutral-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </button>
        )}
      </div>

      {railBody}
    </>
  );
}
